import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Package, Sparkles, MapPin } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function MaterialOrders() {
  const { orgId } = useAuth();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orgId) {
      loadOrders();
    }
  }, [orgId]);

  const loadOrders = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("material_orders")
        .select("*, job:jobs(job_number, property_address)")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error("Error loading orders:", error);
      toast.error("Failed to load material orders");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId: string, newStatus: string) => {
    try {
      const updates: any = { status: newStatus };
      
      if (newStatus === "ordered") {
        updates.ordered_at = new Date().toISOString();
      } else if (newStatus === "delivered") {
        updates.delivered_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from("material_orders")
        .update(updates)
        .eq("id", orderId);

      if (error) throw error;

      toast.success("Order status updated");
      loadOrders();
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error("Failed to update order status");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft":
        return "default";
      case "ordered":
        return "secondary";
      case "confirmed":
        return "default";
      case "delivered":
        return "outline";
      case "cancelled":
        return "destructive";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading material orders...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Package className="w-8 h-8" />
          Material Orders
        </h1>
        <p className="text-muted-foreground">Track and manage material orders for jobs</p>
      </div>

      {orders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">No material orders yet</p>
            <p className="text-sm text-muted-foreground">
              Use the Material Calculator in job details to create orders
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <CardTitle className="flex items-center gap-2">
                        {order.supplier || "No Supplier"}
                        {order.ai_suggested && (
                          <Badge variant="outline" className="gap-1">
                            <Sparkles className="w-3 h-3" />
                            AI
                          </Badge>
                        )}
                      </CardTitle>
                      <Badge variant={getStatusColor(order.status)}>
                        {order.status}
                      </Badge>
                    </div>
                    <CardDescription className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      Job {order.job?.job_number}: {order.job?.property_address}
                    </CardDescription>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      ${order.total_cost?.toFixed(2) || "0.00"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {order.items?.length || 0} items
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {order.items?.map((item: any, idx: number) => (
                    <div key={idx} className="p-2 flex justify-between text-sm">
                      <span>
                        {item.name} ({item.quantity} {item.unit})
                      </span>
                      <span className="font-medium">
                        ${((item.quantity * item.unitPrice) || 0).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 text-sm text-muted-foreground">
                  <div>Created: {format(new Date(order.created_at), "MMM d, yyyy")}</div>
                  {order.ordered_at && (
                    <div>• Ordered: {format(new Date(order.ordered_at), "MMM d, yyyy")}</div>
                  )}
                  {order.delivered_at && (
                    <div>• Delivered: {format(new Date(order.delivered_at), "MMM d, yyyy")}</div>
                  )}
                </div>

                <div className="flex gap-2">
                  {order.status === "draft" && (
                    <Button
                      size="sm"
                      onClick={() => handleStatusUpdate(order.id, "ordered")}
                    >
                      Mark as Ordered
                    </Button>
                  )}
                  {order.status === "ordered" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusUpdate(order.id, "delivered")}
                    >
                      Mark as Delivered
                    </Button>
                  )}
                  {(order.status === "draft" || order.status === "ordered") && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleStatusUpdate(order.id, "cancelled")}
                    >
                      Cancel Order
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
